import type { IgnoreModifier } from '@apollo/client/cache'

import { graphql, maskFragments } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { client } from '~publish/legacy/apollo-client'

import { PostCardNotes_Note } from './fragments/PostCardNotes_Note'
import { UpdatedNoteOnPost } from './useAddNoteToPost'
import { useTypedMutation } from '../../hooks/useTypedMutation'

export const UpdateNoteOnPost = graphql(
  `
    mutation UpdateNoteOnPost(
      $postId: PostId!
      $noteId: NoteId!
      $text: String!
    ) {
      updateNoteOnPost(
        input: { postId: $postId, noteId: $noteId, text: $text }
      ) {
        __typename
        ... on UpdateNoteOnPostSuccess {
          post {
            id
            notes {
              ...PostCardNotes_Note
            }
          }
        }
        ... on MutationError {
          message
        }
      }
    }
  `,
  [PostCardNotes_Note],
)

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export const useUpdateNoteOnPost = () =>
  useTypedMutation(UpdateNoteOnPost, (data) => data.updateNoteOnPost, {
    successTypename: 'UpdateNoteOnPostSuccess',
    optimisticResponse: (
      { postId, noteId, text },
      { IGNORE },
    ):
      | {
          updateNoteOnPost: {
            __typename: 'UpdateNoteOnPostSuccess'
            post: {
              __typename: 'Post'
              id: string
              notes: FragmentOf<typeof PostCardNotes_Note>[]
            }
          }
        }
      | IgnoreModifier => {
      const existingPost = client.readFragment({
        id: client.cache.identify({ __typename: 'Post', id: postId }),
        fragment: UpdatedNoteOnPost,
      })

      if (!existingPost) {
        return IGNORE
      }
      const updatedNotes = existingPost.notes.map((note) =>
        maskFragments(
          [PostCardNotes_Note],
          note.id === noteId
            ? { ...note, text, updatedAt: new Date().toISOString() }
            : note,
        ),
      )

      return {
        updateNoteOnPost: {
          __typename: 'UpdateNoteOnPostSuccess',
          post: {
            id: postId,
            __typename: 'Post',
            notes: updatedNotes,
          },
        },
      }
    },
  })
